import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import FamilyMemberModal from '../components/FamilyMemberModal';
import tankerkoenigService from '../services/tankerkoenigService';

const { 
  FiUsers, FiCreditCard, FiShield, FiDatabase, FiTrash2, 
  FiPlus, FiEdit2, FiDroplet, FiSearch, FiRefreshCw, FiAlertCircle 
} = FiIcons;

export default function Settings() {
  const { state, dispatch, enhancedDispatch } = useFamilyContext();
  const { familyProfile, isAdmin, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('family');
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  // Tankerkoenig State
  const [fuelConfig, setFuelConfig] = useState({
    enabled: false, apiKey: '', lat: 52.52, lng: 13.405, radius: 5, fuelType: 'e5', subscribedStations: []
  });
  const [searchAddress, setSearchAddress] = useState('');
  const [isGeocoding, setIsGeocoding] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      if (!familyProfile?.family_id) return;
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'tankerkoenig_config')
        .eq('family_id', familyProfile.family_id)
        .maybeSingle();
      
      if (data?.value) setFuelConfig(data.value);
    };
    loadConfig();
  }, [familyProfile?.family_id]);

  const saveFuelConfig = async (newConfig) => {
    if (!familyProfile?.family_id) return;
    setFuelConfig(newConfig);
    await supabase
      .from('app_settings')
      .upsert({
        key: 'tankerkoenig_config',
        value: newConfig,
        family_id: familyProfile.family_id,
        updated_at: new Date().toISOString()
      });
  };

  const handleGeocode = async () => {
    if (!searchAddress) return;
    setIsGeocoding(true);
    try {
      const result = await tankerkoenigService.geocodeAddress(searchAddress);
      if (result) {
        const updated = { ...fuelConfig, lat: result.lat, lng: result.lng };
        await saveFuelConfig(updated);
        alert(`Standort aktualisiert: ${result.display_name}`);
      }
    } catch (e) {
      alert("Adresse nicht gefunden.");
    } finally {
      setIsGeocoding(false);
    }
  };

  const tabs = [
    { id: 'family', label: 'Familie', icon: FiUsers },
    { id: 'integrations', label: 'Integrationen', icon: FiDroplet },
    { id: 'subscription', label: 'Abonnement', icon: FiCreditCard },
    { id: 'danger', label: 'Gefahrenzone', icon: FiAlertCircle },
  ];

  return (
    <div className="max-w-4xl mx-auto pb-20">
      <header className="mb-8 px-4 lg:px-0">
        <h1 className="text-3xl font-bold dark:text-white transition-colors">Einstellungen</h1>
        <p className="text-gray-500 dark:text-gray-400">Verwalte deine Familie und App-Präferenzen</p>
      </header>

      <div className="flex flex-col md:flex-row gap-8">
        <aside className="w-full md:w-64 space-y-1 px-4 lg:px-0">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-5 h-5 mr-3" />
              {tab.label}
            </button>
          ))}
        </aside>

        <div className="flex-1 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm p-6 lg:p-8 transition-colors">
          <AnimatePresence mode="wait">
            {activeTab === 'family' && (
              <motion.div key="family" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="space-y-8">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold dark:text-white">Familienmitglieder</h2>
                  {(isAdmin || isSuperAdmin) && (
                    <button
                      onClick={() => { setEditingMember(null); setIsMemberModalOpen(true); }}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 transition-all"
                    >
                      <SafeIcon icon={FiPlus} className="mr-2" /> Hinzufügen
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  {state.familyMembers?.map(member => (
                    <div key={member.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700 group">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-inner" style={{ backgroundColor: member.color || '#3B82F6' }}>
                          {member.avatar || member.name?.charAt(0)}
                        </div>
                        <div>
                          <h4 className="font-bold dark:text-white">{member.name}</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{member.role}</p>
                        </div>
                      </div>
                      {(isAdmin || isSuperAdmin) && (
                        <button onClick={() => { setEditingMember(member); setIsMemberModalOpen(true); }} className="p-2 text-gray-400 hover:text-blue-500 transition-colors">
                          <SafeIcon icon={FiEdit2} className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {activeTab === 'integrations' && (
              <motion.div key="integrations" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="space-y-8">
                <div>
                  <h2 className="text-xl font-bold dark:text-white flex items-center">
                    <SafeIcon icon={FiDroplet} className="mr-2 text-blue-500" /> Spritpreise (Tankerkönig)
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Konfiguriere deine Tankstellen-Anzeige</p>
                </div>

                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">
                    <div>
                      <h4 className="font-bold dark:text-white">Feature Aktivieren</h4>
                      <p className="text-xs text-gray-500">Zeigt Spritpreise auf dem Dashboard</p>
                    </div>
                    <button
                      onClick={() => saveFuelConfig({ ...fuelConfig, enabled: !fuelConfig.enabled })}
                      className={`w-12 h-6 rounded-full transition-all relative ${fuelConfig.enabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-700'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${fuelConfig.enabled ? 'left-7' : 'left-1'}`} />
                    </button>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold dark:text-white">API Key</label>
                    <input
                      type="password"
                      value={fuelConfig.apiKey}
                      onChange={(e) => setFuelConfig({ ...fuelConfig, apiKey: e.target.value })}
                      onBlur={() => saveFuelConfig(fuelConfig)}
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white transition-all"
                      placeholder="Dein Tankerkönig Key"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold dark:text-white">Standort festlegen</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={searchAddress}
                        onChange={(e) => setSearchAddress(e.target.value)}
                        placeholder="Stadt oder Adresse eingeben"
                        className="flex-1 px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl outline-none dark:text-white"
                      />
                      <button onClick={handleGeocode} disabled={isGeocoding} className="px-4 bg-blue-600 text-white rounded-xl font-bold disabled:opacity-50">
                        <SafeIcon icon={isGeocoding ? FiRefreshCw : FiSearch} className={isGeocoding ? 'animate-spin' : ''} />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'subscription' && (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-blue-50 dark:bg-blue-900/20 rounded-3xl flex items-center justify-center mx-auto mb-6 text-blue-600">
                  <SafeIcon icon={FiCreditCard} className="w-10 h-10" />
                </div>
                <h2 className="text-2xl font-bold dark:text-white">Active Family Plan</h2>
                <p className="text-gray-500 dark:text-gray-400 mt-2">Nächste Abrechnung am 24.12.2024</p>
              </div>
            )}

            {activeTab === 'danger' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-red-600">Gefahrenzone</h2>
                <div className="space-y-3">
                  <button className="w-full flex items-center justify-between p-4 border border-red-200 dark:border-red-900/30 rounded-2xl hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors">
                    <div className="text-left"><h4 className="font-bold text-red-600">Daten exportieren</h4><p className="text-xs text-gray-500">Backup als JSON laden</p></div>
                    <SafeIcon icon={FiDatabase} className="w-5 h-5 text-red-400" />
                  </button>
                </div>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {isMemberModalOpen && (
        <FamilyMemberModal
          isOpen={isMemberModalOpen}
          onClose={() => { setIsMemberModalOpen(false); setEditingMember(null); }}
          onSave={(member) => {
            if (editingMember) enhancedDispatch({ type: 'UPDATE_FAMILY_MEMBER', payload: member });
            else enhancedDispatch({ type: 'ADD_FAMILY_MEMBER', payload: member });
            setIsMemberModalOpen(false);
          }}
          member={editingMember}
        />
      )}
    </div>
  );
}